import { useEffect, useRef } from 'react'

const resizeTextarea = (textarea: HTMLTextAreaElement, placeholder?: string) => {
  // Reset height to auto to get the correct scrollHeight
  textarea.style.height = 'auto'

  // Set height to scrollHeight, but ensure minimum height of 48px (h-12)
  const minHeight = 48

  // If value is empty and we have a placeholder, measure the placeholder height
  let contentHeight = textarea.scrollHeight

  if (!textarea.value && placeholder) {
    // Temporarily set the value to placeholder to measure its height
    const originalValue = textarea.value
    textarea.value = placeholder
    contentHeight = Math.max(contentHeight, textarea.scrollHeight)
    textarea.value = originalValue
  }

  // Add a small buffer to ensure text doesn't get cut off
  const newHeight = Math.max(contentHeight + 2, minHeight)
  textarea.style.height = `${newHeight}px`
}

export const useAutoResizeTextarea = (value: string, placeholder?: string) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    resizeTextarea(textarea, placeholder)
  }, [value, placeholder])

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    // Also handle input events for real-time resizing as user types
    const handleInput = () => {
      resizeTextarea(textarea)
    }

    textarea.addEventListener('input', handleInput)
    return () => {
      textarea.removeEventListener('input', handleInput)
    }
  }, [])

  return textareaRef
}
